import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import RutaSearch from '../../components/RutaSearch';
import ListaPrecioSearch from '../../components/ListaPrecioSearch';
import ProductoSearch from '../../components/ProductoSearch';
import VendedorSelect from '../../components/VendedorSelect';
import descuentoService, {
  CreateDescuentoDto,
  DescuentoDto,
  descuentoKeyId,
  fromEditSearch,
  fromSearchParams,
  toCreatePayload,
} from '../../services/descuentoService';
import { catalogosService } from '../../services/catalogosService';
import productoService from '../../services/productoService';
import clienteService from '../../services/clienteService';
import rutaService from '../../services/rutaService';
import listaPrecioService from '../../services/listaPrecioService';
import { Clase, Linea, Ramo } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';
import { ListaPrecio } from '../../services/listaPrecioService';
import { Ruta } from '../../services/rutaService';

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

const pagedItems = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  const o = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const items = o.items ?? o.Items;
  return Array.isArray(items) ? (items as T[]) : [];
};

const catalogStr = (item: unknown, camel: string, pascal: string): string => {
  const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
  return String(o[camel] ?? o[pascal] ?? '').trim();
};

const emptyForm = (): CreateDescuentoDto => ({
  codigoLista: '',
  nroCliente: 0,
  codigoRuta: 0,
  nroVendedor: 0,
  atributo1Cli: '',
  atributo2Cli: '',
  codigoArticulo: '',
  codigoLinea: '',
  codigoRamo: '',
  codigoClase: '',
  atributo1Sto: '',
  atributo2Sto: '',
  porcDesc: 0,
  fechaVigencia: null,
});

const dtoToForm = (d: DescuentoDto): CreateDescuentoDto => ({
  ...emptyForm(),
  codigoLista: d.codigoLista ?? '',
  nroCliente: Number(d.nroCliente) || 0,
  codigoRuta: Number(d.codigoRuta) || 0,
  nroVendedor: Number(d.nroVendedor) || 0,
  atributo1Cli: d.atributo1Cli ?? '',
  atributo2Cli: d.atributo2Cli ?? '',
  codigoArticulo: d.codigoArticulo ?? '',
  codigoLinea: d.codigoLinea ?? '',
  codigoRamo: d.codigoRamo ?? '',
  codigoClase: d.codigoClase ?? '',
  atributo1Sto: d.atributo1Sto ?? '',
  atributo2Sto: d.atributo2Sto ?? '',
  porcDesc: Number(d.porcDesc) || 0,
  fechaVigencia: d.fechaVigencia ?? null,
});

const labelsBasicos = (d: Pick<CreateDescuentoDto, 'nroCliente' | 'codigoRuta' | 'codigoLista' | 'codigoArticulo'>) => ({
  cliente: Number(d.nroCliente) > 0 ? `Cliente ${d.nroCliente}` : '',
  ruta: Number(d.codigoRuta) > 0 ? `Ruta ${d.codigoRuta}` : '',
  lista: (d.codigoLista ?? '').trim(),
  articulo: (d.codigoArticulo ?? '').trim(),
});

const dtoTieneDatos = (d: DescuentoDto): boolean =>
  descuentoKeyId(d) !== descuentoKeyId(emptyForm()) ||
  Number(d.porcDesc) !== 0 ||
  Boolean(String(d.fechaVigencia ?? '').trim());

async function enrichLabels(d: CreateDescuentoDto): Promise<ReturnType<typeof labelsBasicos>> {
  const base = labelsBasicos(d);
  const [cliente, ruta, lista, articulo] = await Promise.all([
    Number(d.nroCliente) > 0
      ? clienteService
          .getCliente(Number(d.nroCliente))
          .then((c) => `${c.nroCliente} - ${c.nombre || c.razonSocial || ''}`.trim())
          .catch(() => base.cliente)
      : Promise.resolve(''),
    Number(d.codigoRuta) > 0
      ? rutaService
          .getRuta(Number(d.codigoRuta))
          .then((r) => `${r.codigoRuta}${r.nombreRuta ? ` - ${r.nombreRuta}` : ''}`)
          .catch(() => base.ruta)
      : Promise.resolve(''),
    (d.codigoLista ?? '').trim()
      ? listaPrecioService
          .getListaPrecio((d.codigoLista ?? '').trim())
          .then((l) => {
            const codigo = (l.codigoLista ?? d.codigoLista ?? '').trim();
            return codigo ? `${codigo}${l.descripcion ? ` - ${l.descripcion}` : ''}` : base.lista;
          })
          .catch(() => base.lista)
      : Promise.resolve(''),
    (d.codigoArticulo ?? '').trim()
      ? productoService
          .getProducto((d.codigoArticulo ?? '').trim())
          .then((p) => {
            const codigo = String(p.codigoArticulo ?? d.codigoArticulo ?? '').trim();
            const desc = String(p.descripcion ?? '').trim();
            return codigo ? `${codigo}${desc ? ` - ${desc}` : ''}` : base.articulo;
          })
          .catch(() => base.articulo)
      : Promise.resolve(''),
  ]);
  return { cliente, ruta, lista, articulo };
}

const toDateInput = (value?: string | null): string => {
  if (!value) return '';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const LookupField: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  onSearch: () => void;
  onClear: () => void;
}> = ({ label, value, disabled, onSearch, onClear }) => (
  <TextField
    label={label}
    value={value}
    fullWidth
    disabled={disabled}
    InputProps={{
      readOnly: true,
      endAdornment: !disabled ? (
        <InputAdornment position="end">
          {value ? (
            <IconButton size="small" onClick={onClear} edge="end" aria-label={`Limpiar ${label}`}>
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : null}
          <IconButton size="small" onClick={onSearch} edge="end" aria-label={`Buscar ${label}`}>
            <SearchIcon />
          </IconButton>
        </InputAdornment>
      ) : undefined,
    }}
    sx={fieldSx}
  />
);

const DescuentoForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = location.pathname.endsWith('/editar');

  const [formData, setFormData] = useState<CreateDescuentoDto>(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [labels, setLabels] = useState({ cliente: '', ruta: '', lista: '', articulo: '' });

  const [clienteOpen, setClienteOpen] = useState(false);
  const [rutaOpen, setRutaOpen] = useState(false);
  const [listaOpen, setListaOpen] = useState(false);
  const [articuloOpen, setArticuloOpen] = useState(false);

  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [clases, setClases] = useState<Clase[]>([]);

  const loadCatalogos = useCallback(async () => {
    try {
      const [r, l, c] = await Promise.all([
        catalogosService.getRamos(1, 1000),
        catalogosService.getLineas(1, 1000),
        catalogosService.getClases(1, 1000),
      ]);
      setRamos(
        pagedItems<Ramo>(r).map((item) => ({
          ...item,
          codigoRamo: catalogStr(item, 'codigoRamo', 'CodigoRamo'),
          nombreRamo: catalogStr(item, 'nombreRamo', 'NombreRamo'),
        })),
      );
      setLineas(
        pagedItems<Linea>(l).map((item) => ({
          ...item,
          codigoLinea: catalogStr(item, 'codigoLinea', 'CodigoLinea'),
          nombreLinea: catalogStr(item, 'nombreLinea', 'NombreLinea'),
          codigoRamo: catalogStr(item, 'codigoRamo', 'CodigoRamo'),
        })),
      );
      setClases(
        pagedItems<Clase>(c).map((item) => ({
          ...item,
          codigoClase: catalogStr(item, 'codigoClase', 'CodigoClase'),
          nombreClase: catalogStr(item, 'nombreClase', 'NombreClase'),
          codigoRamo: catalogStr(item, 'codigoRamo', 'CodigoRamo'),
          codigoLinea: catalogStr(item, 'codigoLinea', 'CodigoLinea'),
        })),
      );
    } catch {
      setRamos([]);
      setLineas([]);
      setClases([]);
    }
  }, []);

  const loadDescuento = useCallback(async () => {
    if (!isEdit) return;
    const sp = new URLSearchParams(location.search);
    const fromState = (location.state as { descuento?: DescuentoDto } | null)?.descuento;
    const fromQuery = fromEditSearch(sp);
    const key = fromSearchParams(sp);
    const seedDto = fromState ?? (dtoTieneDatos(fromQuery) ? fromQuery : null);
    const seed = seedDto ? dtoToForm(seedDto) : null;

    if (seed) {
      setFormData(seed);
      setLabels(labelsBasicos(seed));
    } else {
      setLoading(true);
    }

    const aplicar = async (d: DescuentoDto) => {
      const form = dtoToForm(d);
      setFormData(form);
      setLabels(labelsBasicos(form));
      try {
        setLabels(await enrichLabels(form));
      } catch {
        /* etiquetas básicas */
      }
    };

    try {
      try {
        const remote = await descuentoService.getByKey(key);
        if (remote && dtoTieneDatos(remote)) {
          await aplicar(remote);
          return;
        }
      } catch {
        /* by-key puede fallar si el backend trata vacío distinto de null */
      }

      try {
        const page = await descuentoService.getDescuentos({ pageNumber: 1, pageSize: 500, ...key });
        const match = (page.items ?? []).find((item) => descuentoKeyId(item) === descuentoKeyId(key));
        if (match) {
          await aplicar(match);
          return;
        }
      } catch {
        /* listado */
      }

      if (seed) {
        try {
          setLabels(await enrichLabels(seed));
        } catch {
          /* ya están las etiquetas básicas */
        }
        return;
      }

      enqueueSnackbar('No se encontró el descuento', { variant: 'error' });
      navigate('/inventario/descuentos');
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, isEdit, location.search, location.state, navigate]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    loadDescuento();
  }, [loadDescuento]);

  const lineasFiltradas = useMemo(() => {
    const ramo = (formData.codigoRamo ?? '').trim();
    if (!ramo) return lineas;
    return lineas.filter((l) => (l.codigoRamo ?? '').trim() === ramo);
  }, [formData.codigoRamo, lineas]);

  const clasesFiltradas = useMemo(() => {
    const ramo = (formData.codigoRamo ?? '').trim();
    const linea = (formData.codigoLinea ?? '').trim();
    return clases.filter((c) => {
      if (ramo && (c.codigoRamo ?? '').trim() !== ramo) return false;
      if (linea && (c.codigoLinea ?? '').trim() !== linea) return false;
      return true;
    });
  }, [clases, formData.codigoLinea, formData.codigoRamo]);

  const withCurrentOption = (
    options: { value: string; label: string }[],
    current: string,
  ) => {
    const value = current.trim();
    if (!value || options.some((o) => o.value === value)) return options;
    return [{ value, label: value }, ...options];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const porc = Number(formData.porcDesc);
    if (!Number.isFinite(porc)) {
      enqueueSnackbar('Indicá el porcentaje de descuento', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const payload = toCreatePayload(formData);
      if (isEdit) {
        await descuentoService.update(payload);
      } else {
        await descuentoService.create(payload);
      }
      enqueueSnackbar('Descuento guardado exitosamente', { variant: 'success' });
      navigate('/inventario/descuentos');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar el descuento'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const disabledKey = isEdit;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inventario/descuentos')} sx={{ textTransform: 'none' }}>
              Descuentos
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Modificando un descuento' : 'Nuevo descuento'}
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Completá solo las dimensiones a las que aplica. Vacío no filtra por ese criterio: puede ser un cliente, una ruta, un artículo, una lista, o cualquier combinación. El backend valida la regla.
          </Typography>

          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const tag = (e.target as HTMLElement).tagName;
              if (tag !== 'BUTTON' && tag !== 'TEXTAREA') e.preventDefault();
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Aplica a
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <LookupField
                  label="Cliente"
                  value={labels.cliente}
                  disabled={disabledKey}
                  onSearch={() => setClienteOpen(true)}
                  onClear={() => {
                    setFormData((p) => ({ ...p, nroCliente: 0 }));
                    setLabels((p) => ({ ...p, cliente: '' }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LookupField
                  label="Ruta"
                  value={labels.ruta}
                  disabled={disabledKey}
                  onSearch={() => setRutaOpen(true)}
                  onClear={() => {
                    setFormData((p) => ({ ...p, codigoRuta: 0 }));
                    setLabels((p) => ({ ...p, ruta: '' }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <VendedorSelect
                  label="Vendedor"
                  value={Number(formData.nroVendedor) > 0 ? Number(formData.nroVendedor) : null}
                  onChange={(nro) => setFormData((p) => ({ ...p, nroVendedor: nro ?? 0 }))}
                  allowEmpty
                  emptyOptionLabel="Sin vendedor"
                  disabled={disabledKey}
                  margin="none"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LookupField
                  label="Lista de precios"
                  value={labels.lista}
                  disabled={disabledKey}
                  onSearch={() => setListaOpen(true)}
                  onClear={() => {
                    setFormData((p) => ({ ...p, codigoLista: '' }));
                    setLabels((p) => ({ ...p, lista: '' }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <LookupField
                  label="Artículo"
                  value={labels.articulo}
                  disabled={disabledKey}
                  onSearch={() => setArticuloOpen(true)}
                  onClear={() => {
                    setFormData((p) => ({ ...p, codigoArticulo: '' }));
                    setLabels((p) => ({ ...p, articulo: '' }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Ramo"
                  allowEmpty
                  emptyOptionLabel="Sin ramo"
                  disabled={disabledKey}
                  value={(formData.codigoRamo || '').trim() || null}
                  onChange={(v) =>
                    setFormData((p) => ({
                      ...p,
                      codigoRamo: (v ?? '').trim(),
                      codigoLinea: '',
                      codigoClase: '',
                    }))
                  }
                  options={withCurrentOption(
                    ramos.map((r) => ({
                      value: (r.codigoRamo ?? '').trim(),
                      label: `${(r.codigoRamo ?? '').trim()} - ${r.nombreRamo}`,
                    })),
                    formData.codigoRamo || '',
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Línea"
                  allowEmpty
                  emptyOptionLabel="Sin línea"
                  disabled={disabledKey}
                  value={(formData.codigoLinea || '').trim() || null}
                  onChange={(v) => setFormData((p) => ({ ...p, codigoLinea: (v ?? '').trim(), codigoClase: '' }))}
                  options={withCurrentOption(
                    lineasFiltradas.map((l) => ({
                      value: (l.codigoLinea ?? '').trim(),
                      label: `${(l.codigoLinea ?? '').trim()} - ${l.nombreLinea}`,
                    })),
                    formData.codigoLinea || '',
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Clase"
                  allowEmpty
                  emptyOptionLabel="Sin clase"
                  disabled={disabledKey}
                  value={(formData.codigoClase || '').trim() || null}
                  onChange={(v) => setFormData((p) => ({ ...p, codigoClase: (v ?? '').trim() }))}
                  options={withCurrentOption(
                    clasesFiltradas.map((c) => ({
                      value: (c.codigoClase ?? '').trim(),
                      label: `${(c.codigoClase ?? '').trim()} - ${c.nombreClase}`,
                    })),
                    formData.codigoClase || '',
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Atributo 1 cliente"
                  value={formData.atributo1Cli || ''}
                  disabled={disabledKey}
                  onChange={(e) => setFormData((p) => ({ ...p, atributo1Cli: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Atributo 2 cliente"
                  value={formData.atributo2Cli || ''}
                  disabled={disabledKey}
                  onChange={(e) => setFormData((p) => ({ ...p, atributo2Cli: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Atributo 1 artículo"
                  value={formData.atributo1Sto || ''}
                  disabled={disabledKey}
                  onChange={(e) => setFormData((p) => ({ ...p, atributo1Sto: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Atributo 2 artículo"
                  value={formData.atributo2Sto || ''}
                  disabled={disabledKey}
                  onChange={(e) => setFormData((p) => ({ ...p, atributo2Sto: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 4, mb: 1.5 }}>
              Descuento
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Porcentaje"
                  type="number"
                  required
                  value={formData.porcDesc === 0 || formData.porcDesc ? formData.porcDesc : ''}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      porcDesc: e.target.value === '' ? 0 : Number(e.target.value),
                    }))
                  }
                  fullWidth
                  inputProps={{ step: 0.01 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Vigente hasta"
                  type="date"
                  value={toDateInput(formData.fechaVigencia)}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      fechaVigencia: e.target.value ? e.target.value : null,
                    }))
                  }
                  fullWidth
                  helperText="Válido todo ese día; al siguiente ya no aplica. Vacío = sin fecha límite."
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/descuentos')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                ESC-Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ClienteSearch
        open={clienteOpen}
        onClose={() => setClienteOpen(false)}
        onSelect={(cliente) => {
          setFormData((p) => ({ ...p, nroCliente: cliente.nroCliente }));
          setLabels((p) => ({ ...p, cliente: `${cliente.nroCliente} - ${cliente.nombre || cliente.razonSocial || ''}` }));
          setClienteOpen(false);
        }}
      />
      <RutaSearch
        open={rutaOpen}
        onClose={() => setRutaOpen(false)}
        onSelect={(ruta: Ruta) => {
          const codigo = Number(ruta.codigoRuta) || 0;
          setFormData((p) => ({ ...p, codigoRuta: codigo }));
          setLabels((p) => ({
            ...p,
            ruta: codigo ? `${codigo}${ruta.nombreRuta ? ` - ${ruta.nombreRuta}` : ''}` : '',
          }));
          setRutaOpen(false);
        }}
      />
      <ListaPrecioSearch
        open={listaOpen}
        onClose={() => setListaOpen(false)}
        onSelect={(lista: ListaPrecio) => {
          const codigo = (lista.codigoLista ?? '').trim();
          setFormData((p) => ({ ...p, codigoLista: codigo }));
          setLabels((p) => ({
            ...p,
            lista: codigo ? `${codigo}${lista.descripcion ? ` - ${lista.descripcion}` : ''}` : '',
          }));
          setListaOpen(false);
        }}
      />

      <ProductoSearch
        open={articuloOpen}
        onClose={() => setArticuloOpen(false)}
        onSelect={(producto) => {
          const codigo = String(producto.codigoArticulo ?? '').trim();
          const desc = String(producto.descripcion ?? '').trim();
          setFormData((p) => ({ ...p, codigoArticulo: codigo }));
          setLabels((p) => ({
            ...p,
            articulo: codigo ? `${codigo}${desc ? ` - ${desc}` : ''}` : '',
          }));
        }}
      />
    </Box>
  );
};

export default DescuentoForm;